import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Award, Calendar, Clock, Monitor, CheckCircle2 } from "lucide-react";
import { useCart } from "../../context/CartContext";
import { useRefCodeManagement } from "../../hooks/useRefCodeManagement";
import useFetchPrograms from "../../hooks/useFetchPrograms";

const PROGRAM_SLUGS = [
  "research-programmes-at-cambridge-entrepreneurship",
  "research-programmes-at-cambridge-artificial-intelligence",
  "research-programmes-at-cambridge-medicine",
];

const ProgramSelection: React.FC = () => {
  const navigate = useNavigate();
  const { clearAndEnroll } = useCart();
  const { navigateWithRefCode } = useRefCodeManagement();

  const { programs, loading } = useFetchPrograms(PROGRAM_SLUGS);

  if (loading) {
    return (
      <section id="program-selection" className="mt-20 py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading programs...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <motion.section
      id="program-selection"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.4 }}
      className="mt-20 py-16 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 tracking-tight">
            Choose Your Cambridge Journey
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Select the program that best fits your academic goals and timeline.
          </p>
        </motion.div>

        {/* Program Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10">
          {programs.map((prog, index) => (
            <motion.div
              key={prog.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 + index * 0.1 }}
              whileHover={{
                y: -5,
                boxShadow:
                  "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                transition: {
                  type: "spring",
                  stiffness: 400,
                  damping: 25,
                },
              }}
              className="relative bg-white rounded-2xl shadow-lg overflow-hidden border-2 border-gray-300"
            >
              <div className="p-8">
                {/* Icon */}
                <div className="w-12 h-12 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-lg flex items-center justify-center mb-6">
                  <Award className="h-6 w-6 text-white" />
                </div>

                {/* Program Name */}
                <h3 className="text-sm md:text-base font-bold text-gray-900 mb-2 leading-tight tracking-tight uppercase">
                  {prog.title}
                </h3>

                {/* Price and Duration */}
                <div className="mb-4">
                  <span className="text-3xl font-black text-gray-900">
                    {prog.formatted_current_price || prog.formatted_price}
                  </span>
                  {prog.is_early_bird_active && prog.formatted_current_price && (
                    <span className="text-sm text-gray-400 line-through ml-2">
                      {prog.formatted_price}
                    </span>
                  )}
                  <span className="text-gray-500 ml-2">• {prog.duration}</span>
                </div>

                {/* Ticket Availability */}
                {prog.available_tickets != null && (
                  <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-gray-700">
                        Availability
                      </span>
                      <span className="text-xs font-bold text-blue-600">
                        {prog.remaining_tickets} / {prog.available_tickets} spots
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div
                        className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                        style={{
                          width: `${
                            ((prog.sold_tickets || 0) /
                              prog.available_tickets) *
                            100
                          }%`,
                        }}
                      ></div>
                    </div>
                    {prog.remaining_tickets != null &&
                      prog.remaining_tickets <= 10 && (
                        <p className="text-red-600 text-xs font-medium mt-1">
                          ⚡ Limited spots remaining!
                        </p>
                      )}
                  </div>
                )}

                {/* Program Details */}
                <div className="space-y-3 mb-8 pb-6 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 text-[#9f162e] mr-2" />
                      <span className="text-gray-700 font-medium">Dates</span>
                    </div>
                    <span className="text-gray-900 font-semibold">
                      To Be Announced
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Clock className="h-4 w-4 text-[#9f162e] mr-2" />
                      <span className="text-gray-700 font-medium">Length</span>
                    </div>
                    <span className="text-gray-900 font-semibold">
                      {prog.duration}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Monitor className="h-4 w-4 text-[#9f162e] mr-2" />
                      <span className="text-gray-700 font-medium">Format</span>
                    </div>
                    <span className="text-gray-900 font-semibold">
                      Hybrid
                    </span>
                  </div>
                </div>

                {/* Features List */}
                <ul className="space-y-3 mb-8 min-h-[280px]">
                  {prog.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-sm leading-relaxed text-gray-700">
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* Enroll Now Button */}
                <button
                  onClick={() => {
                    clearAndEnroll(prog, 1);
                    navigate(navigateWithRefCode("/cart"));
                  }}
                  disabled={prog.is_sold_out}
                  className={`w-full font-bold py-3 px-6 rounded-lg transition-all duration-300 transform shadow-md hover:shadow-lg ${
                    prog.is_sold_out
                      ? "bg-gray-400 text-white cursor-not-allowed"
                      : "bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white hover:scale-[1.02]"
                  }`}
                >
                  {prog.is_sold_out ? "Sold Out" : "Enroll Now"}
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.section>
  );
};

export default ProgramSelection;
